// TotalStateAdminTransactionReport.jsx

import React, { useEffect, useState, useRef } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  Backdrop,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { DataGrid } from '@mui/x-data-grid';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { format } from 'date-fns';
import { formatDateTimeIST } from '../../utils/dateFormat';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';
// [CASH-MODE] UPI / Cash filter + indication
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeChip from '../../components/PaymentModeChip';
import PaymentModeTotals from '../../components/PaymentModeTotals';
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeParam, paymentModeLabel, filterRowsByMode, totalsByModeFromRows } from '../../utils/paymentMode';

const TotalStateAdminTransactionReport = () => {
  const { can } = usePermissions();
  const { userId } = useParams(); // State Admin User ID from URL params
  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 30)) // Last 30 days
  );
  const [endDate, setEndDate] = useState(new Date());
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [shops, setShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState('');
  const [summary, setSummary] = useState({
    totalTransactions: 0,
    totalAmount: 0,
    successfulTransactions: 0,
    failedTransactions: 0
  });
  const [stateAdminInfo, setStateAdminInfo] = useState({
    userLevel: null,
    bypassRestrictions: false,
    totalShops: 0,
    isFiltered: false,
    filteredShops: 0
  });
  const [stateAdmin, setStateAdmin] = useState(null);
  const [loading, setLoading] = useState(false);
  const [shopsLoading, setShopsLoading] = useState(false);
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]
  const [totalsByMode, setTotalsByMode] = useState(null); // [CASH-MODE]

  const componentRef = useRef();

  // Get current user token for headers
  const token = sessionStorage.getItem('token');

  useEffect(() => {
    if (userId) {
      fetchShopsUnderStateAdmin();
      fetchTransactionData();
    } else {
      setDialogMessage('State Admin User ID is missing from the URL. Please check the URL and try again.');
      setOpenDialog(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Fetch transactions when dates or shop selection changes
  useEffect(() => {
    if (userId) {
      fetchTransactionData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, selectedShop, paymentMode]); // [CASH-MODE] paymentMode

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleShopChange = (event) => {
    setSelectedShop(event.target.value);
  };

  const handleResetFilter = () => {
    setSelectedShop('');
  };

  const validateDates = () => {
    if (!startDate || !endDate) {
      setDialogMessage('Please select both start date and end date.');
      setOpenDialog(true);
      return false;
    }
    const diffInDays = Math.ceil(
      (endDate - startDate) / (1000 * 60 * 60 * 24)
    );
    if (startDate > endDate) {
      setDialogMessage('Start date must be on or before the end date.');
      setOpenDialog(true);
      return false;
    }
    if (diffInDays > 31) {
      setDialogMessage('Date range cannot be more than 31 days.');
      setOpenDialog(true);
      return false;
    }
    return true;
  };

  const fetchShopsUnderStateAdmin = async () => {
    setShopsLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/stateAdminReport/getShopsUnderStateAdmin/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200 && response.data.status === 'SUCCESS') {
        setShops(response.data.data.shops || []);
      }
    } catch (error) {
      console.error('Error fetching shops:', error);
      setDialogMessage('Failed to fetch shops under State Admin supervision.');
      setOpenDialog(true);
    }
    setShopsLoading(false);
  };

  const fetchTransactionData = async () => {
    if (!validateDates()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(endDate, 'yyyy-MM-dd');
      
      const requestData = {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        ...(selectedShop && { shopId: selectedShop }),
        paymentMode: paymentModeParam(paymentMode) // [CASH-MODE]
      };
      
      const apiUrl = `${API_BASE_URL}/stateAdminReport/getTransactions/${userId}`;
      
      const response = await axios.post(
        apiUrl,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (response.status === 200 && response.data.status === 'SUCCESS') {
        const {
          transactionsList,
          summary: responseSummary,
          userLevel,
          bypassRestrictions,
          totalShops,
          filteredShops,
          isFiltered
        } = response.data.data;
        
        // [CASH-MODE] client-side guard in case the API ignores the filter
        setTransactions(filterRowsByMode(transactionsList || [], paymentMode));
        setTotalsByMode(response.data.data.totalsByMode || (responseSummary && responseSummary.totalsByMode) || null);
        setSummary(responseSummary || {
          totalTransactions: 0,
          totalAmount: 0,
          successfulTransactions: 0,
          failedTransactions: 0
        });
        setStateAdminInfo({
          userLevel,
          bypassRestrictions,
          totalShops: totalShops || 0,
          isFiltered,
          filteredShops: filteredShops || 0
        });
      } else {
        console.error('Failed to fetch transactions');
        setTransactions([]);
        setSummary({
          totalTransactions: 0,
          totalAmount: 0,
          successfulTransactions: 0,
          failedTransactions: 0
        });
      }
    } catch (error) {
      console.error('=== API Call Error ===');
      console.error('Error object:', error);
      console.error('Error response:', error.response?.data);
      
      if (error.response) {
        setDialogMessage(
          error.response.data.message ||
            'An error occurred while fetching transactions.'
        );
        setOpenDialog(true);
      } else if (error.request) {
        setDialogMessage('No response from server. Please check if the backend is running.');
        setOpenDialog(true);
      } else {
        setDialogMessage('Error setting up the request: ' + error.message);
        setOpenDialog(true);
      }
      setTransactions([]);
      setSummary({
        totalTransactions: 0,
        totalAmount: 0,
        successfulTransactions: 0,
        failedTransactions: 0
      });
    }
    
    setLoading(false);
  };

  const formatDate = (dateString) => (dateString ? formatDateTimeIST(dateString) : '');

  // cash payments: never show internal 'CASH-...' references
  const cleanRef = (v) => (!v || v === 'N/A' || /^CASH-/i.test(String(v)) ? '-' : v);

  const handlePrint = async () => {
    if (!can('stateHead.transactions.print')) return;
    if (transactions.length === 0) {
      setDialogMessage('No transactions to print.');
      setOpenDialog(true);
      return;
    }

    try {
      const input = componentRef.current;
      const canvas = await html2canvas(input, {
        scale: 2,
        logging: false,
        useCORS: true,
        windowWidth: 1400,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'pt', 'a4');
      const imgWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`StateAdmin_TransactionReport_${selectedShop ? 'Filtered' : 'All'}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setDialogMessage('An error occurred while generating the PDF.');
      setOpenDialog(true);
    }
  };

  const columns = [
    { field: 'serialNumber', headerName: 'S.No.', width: 70 },
    { field: 'businessName', headerName: 'Business Name', width: 150 },
    { field: 'sellerName', headerName: 'Seller Name', width: 130 },
    { field: 'mobileNumber', headerName: 'Mobile', width: 120 },
    { field: 'city', headerName: 'City', width: 100 },
    // [CASH-MODE]
    { field: 'paymentMode', headerName: 'Mode', width: 90, renderCell: (params) => <PaymentModeChip row={params.row} /> },
    { 
      field: 'transactionAmount', 
      headerName: 'Amount (₹)', 
      width: 120,
      renderCell: (params) => `₹${params.value.toFixed(2)}`
    },
    {
      field: 'transactionDate',
      headerName: 'Date & Time',
      width: 160,
      renderCell: (params) => {
        if (!params.value) return '';
        return <div>{formatDate(params.value)}</div>;
      },
    },
    { 
      field: 'settlementStatus', 
      headerName: 'Status', 
      width: 100,
      renderCell: (params) => (
        <span style={{ 
          color: params.value === 'SUCCESS' ? 'green' : 'red',
          fontWeight: 'bold'
        }}>
          {params.value}
        </span>
      )
    },
    { field: 'payerName', headerName: 'Payer Name', width: 130 },
    { field: 'yppReferenceNumber', headerName: 'YPP Reference', width: 150, renderCell: (params) => cleanRef(params.value) },
  ];

  // [CASH-MODE] prefer API totals, fall back to loaded rows
  const modeTotals = totalsByMode || totalsByModeFromRows(transactions, (t) => t.transactionAmount);
  const modeAmountKey = modeTotals?.UPI?.totalAmount !== undefined ? 'totalAmount' : 'amount';

  const renderTableRows = () => {
    return transactions.map((transaction) => (
      <tr key={transaction.id}>
        <td>{transaction.serialNumber}</td>
        <td>{transaction.businessName}</td>
        <td>{transaction.sellerName}</td>
        <td>{transaction.mobileNumber}</td>
        <td>{transaction.city}</td>
        <td>{paymentModeLabel(transaction)}</td>{/* [CASH-MODE] */}
        <td>₹{transaction.transactionAmount.toFixed(2)}</td>
        <td>{formatDate(transaction.transactionDate)}</td>
        <td>{transaction.settlementStatus}</td>
        <td>{transaction.payerName}</td>
        <td>{cleanRef(transaction.yppReferenceNumber)}</td>
      </tr>
    ));
  };

  const getSelectedShopName = () => {
    if (!selectedShop) return '';
    const shop = shops.find(s => s.shopId === selectedShop);
    return shop ? shop.businessName : '';
  };

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      {/* Place BackButton outside the Grid */}
      <BackButton />
      <br />  <br />
      
      {/* Loading Backdrop */}
      <Backdrop 
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2
        }} 
        open={loading}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6" align="center">
          Fetching State Admin Transaction Data...
        </Typography>
        <Typography variant="body2" align="center" sx={{ maxWidth: 400 }}>
          {selectedShop ? 
            `Processing transactions for selected shop...` :
            `Processing transactions from all shops under State Admin supervision...`
          }
        </Typography>
      </Backdrop>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <div style={{ padding: '20px' }}>
          <Typography variant="h6" style={{ marginTop: '10px' }}>
            State Admin Transaction Report {stateAdminInfo.isFiltered ? '(Filtered)' : '(All Shops)'}
          </Typography>
          
          {/* State Admin (Level 2) details */}
          {stateAdmin && (
            <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>State Admin Details</Typography>
              <Grid container spacing={1} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={4}><Typography variant="body2"><strong>Name:</strong> {stateAdmin.name || '-'}</Typography></Grid>
                <Grid item xs={12} sm={4}><Typography variant="body2"><strong>Mobile:</strong> {stateAdmin.phoneNumber || '-'}</Typography></Grid>
                <Grid item xs={12} sm={4}><Typography variant="body2"><strong>Email:</strong> {stateAdmin.email || '-'}</Typography></Grid>
                <Grid item xs={12} sm={4}><Typography variant="body2"><strong>State Head ID:</strong> {stateAdmin.stateHeadId || '-'}</Typography></Grid>
                <Grid item xs={12} sm={4}><Typography variant="body2"><strong>State Code:</strong> {stateAdmin.stateCode || '-'}</Typography></Grid>
                <Grid item xs={12} sm={4}><Typography variant="body2"><strong>Pincode:</strong> {stateAdmin.pinCode || '-'}</Typography></Grid>
                <Grid item xs={12} sm={8}><Typography variant="body2"><strong>Address:</strong> {stateAdmin.address || '-'}</Typography></Grid>
                <Grid item xs={12} sm={4}><Typography variant="body2"><strong>Status:</strong> {stateAdmin.status || '-'} | <strong>Total Shops:</strong> {stateAdminInfo.totalShops}</Typography></Grid>
              </Grid>
            </Paper>
          )}

          {/* Active Filter Display */}
          {selectedShop && (
            <Box sx={{ marginTop: 1, marginBottom: 2 }}>
              <Chip 
                label={`Filtered by: ${getSelectedShopName()}`}
                color="warning" 
                variant="outlined"
                onDelete={handleResetFilter}
                sx={{ marginRight: 1 }}
              />
            </Box>
          )}

          {/* Info Alert */}
          <Alert severity="info" sx={{ marginTop: 2, marginBottom: 2 }}>
            {selectedShop ? 
              `Showing transactions from selected shop only. Use the shop filter to change or reset to see all shops.` :
              `Showing transactions from ALL shops under State Admin supervision. Use the shop filter to view specific shop data.`
            }
          </Alert>

          <Grid
            container
            spacing={2}
            alignItems="center"
            style={{ marginTop: '20px' }}
          >
            <Grid item xs={12} sm={3}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                renderInput={(params) => <TextField fullWidth {...params} />}
                maxDate={new Date()}
                inputFormat="dd-MM-yyyy"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                renderInput={(params) => <TextField fullWidth {...params} />}
                maxDate={new Date()}
                inputFormat="dd-MM-yyyy"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth disabled={shopsLoading}>
                <InputLabel>Filter by Shop</InputLabel>
                <Select
                  value={selectedShop}
                  label="Filter by Shop"
                  onChange={handleShopChange}
                >
                  <MenuItem value="">
                    <em>All Shops</em>
                  </MenuItem>
                  {shops.map((shop) => (
                    <MenuItem key={shop.shopId} value={shop.shopId}>
                      {shop.businessName} - {shop.city}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                variant="contained"
                color="warning"
                onClick={fetchTransactionData}
                style={{ marginRight: '10px' }}
                disabled={loading}
                fullWidth
              >
                {loading ? 'Loading...' : 'Refresh'}
              </Button>
            </Grid>
          </Grid>

          {/* Additional Controls */}
          <Grid container spacing={2} style={{ marginTop: '10px' }}>
            <Grid item xs={12} sm={6} sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* [CASH-MODE] */}
              <PaymentModeFilter value={paymentMode} onChange={setPaymentMode} />
              {selectedShop && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleResetFilter}
                  disabled={loading}
                >
                  Reset Filter (Show All Shops)
                </Button>
              )}
            </Grid>
            <Grid item xs={12} sm={6} style={{ textAlign: 'right' }}>
              {can('stateHead.transactions.print') && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handlePrint}
                  disabled={transactions.length === 0 || loading}
                >
                  Print Report
                </Button>
              )}
            </Grid>
          </Grid>

          {/* Summary Cards */}
          {transactions.length > 0 && (
            <Grid container spacing={2} style={{ marginTop: '20px' }}>
              <Grid item xs={12} sm={3}>
                <Paper elevation={3} style={{ padding: '15px', textAlign: 'center' }}>
                  <Typography variant="h6" color="warning.main">
                    Total Transactions
                  </Typography>
                  <Typography variant="h4">
                    {summary.totalTransactions}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Paper elevation={3} style={{ padding: '15px', textAlign: 'center' }}>
                  <Typography variant="h6" color="warning.main">
                    Total Amount
                  </Typography>
                  <Typography variant="h4">
                    ₹{summary.totalAmount.toFixed(2)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Paper elevation={3} style={{ padding: '15px', textAlign: 'center', backgroundColor: '#fff8e1' }}>
                  <Typography variant="h6" color="warning.main">
                    Successful
                  </Typography>
                  <Typography variant="h4" style={{ color: '#f57c00' }}>
                    {summary.successfulTransactions}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Paper elevation={3} style={{ padding: '15px', textAlign: 'center', backgroundColor: '#fce8e8' }}>
                  <Typography variant="h6" color="error.main">
                    Failed
                  </Typography>
                  <Typography variant="h4" style={{ color: 'red' }}>
                    {summary.failedTransactions}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* [CASH-MODE] */}
          {transactions.length > 0 && (
            <PaymentModeTotals totals={modeTotals} amountKey={modeAmountKey} title="Transactions" sx={{ mt: 2 }} />
          )}

          {!loading && (
            <div style={{ marginTop: '20px', height: 600, width: '100%' }}>
              {transactions.length > 0 ? (
                <DataGrid
                  rows={transactions}
                  columns={columns}
                  pageSize={25}
                  rowsPerPageOptions={[25, 50, 100]}
                  disableSelectionOnClick
                  loading={loading}
                  pagination
                  paginationMode="client"
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Typography variant="h6" align="center">
                    {selectedShop ? 
                      `No transactions found for the selected shop in the chosen date range.` :
                      `No transactions found for the selected dates.`
                    }
                  </Typography>
                  {stateAdminInfo.totalShops > 0 && (
                    <Alert severity="info" sx={{ marginTop: 2, maxWidth: 600, margin: '16px auto' }}>
                      <Typography variant="body2">
                        <strong>State Admin Info:</strong> You have access to {stateAdminInfo.totalShops} shops. 
                        {selectedShop ? 
                          ' Try expanding your date range or select a different shop.' :
                          ' Try expanding your date range to include more transactions.'
                        }
                      </Typography>
                    </Alert>
                  )}
                </div>
              )}
            </div>
          )}

          <Dialog open={openDialog} onClose={handleDialogClose}>
            <DialogTitle>Message</DialogTitle>
            <DialogContent>
              <Typography>{dialogMessage}</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDialogClose} color="primary">
                OK
              </Button>
            </DialogActions>
          </Dialog>

          {/* Printable Component */}
          <div style={{ position: 'absolute', left: '-10000px', top: 0 }}>
            <div ref={componentRef} style={{ width: '1400px', padding: '20px' }}>
              <Typography variant="h4" align="center">
                Q-Pay Reports
              </Typography>
              <Typography
                variant="h6"
                align="center"
                style={{ marginTop: '10px' }}
              >
                State Admin Transaction Report {selectedShop ? '(Filtered by Shop)' : '(All Shops)'}
              </Typography>
              <Typography
                variant="subtitle1"
                align="center"
                style={{ marginBottom: '10px' }}
              >
                {`From ${format(startDate, 'dd/MM/yyyy')} to ${format(
                  endDate,
                  'dd/MM/yyyy'
                )}`}
              </Typography>
              {selectedShop && (
                <Typography
                  variant="subtitle2"
                  align="center"
                  style={{ marginBottom: '20px' }}
                >
                  Filtered by Shop: {getSelectedShopName()}
                </Typography>
              )}
              
              {/* Summary for Print */}
              <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                <Typography variant="h6">Transaction Summary</Typography>
                <Typography>Total: {summary.totalTransactions} | Amount: ₹{summary.totalAmount.toFixed(2)} | Success: {summary.successfulTransactions} | Failed: {summary.failedTransactions}</Typography>
                {/* [CASH-MODE] */}
                <Typography>
                  UPI: ₹{(Number(modeTotals?.UPI?.[modeAmountKey]) || 0).toFixed(2)} | Cash: ₹{(Number(modeTotals?.CASH?.[modeAmountKey]) || 0).toFixed(2)}
                </Typography>
              </div>

              {transactions.length > 0 && (
                <table className="print-table">
                  <thead>
                    <tr>
                      <th>S.No.</th>
                      <th>Business Name</th>
                      <th>Seller Name</th>
                      <th>Mobile</th>
                      <th>City</th>
                      <th>Mode</th>{/* [CASH-MODE] */}
                      <th>Amount</th>
                      <th>Date & Time</th>
                      <th>Status</th>
                      <th>Payer Name</th>
                      <th>YPP Reference</th>
                    </tr>
                  </thead>
                  <tbody>{renderTableRows()}</tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </LocalizationProvider>
    </Box>
  );
};

export default TotalStateAdminTransactionReport;